import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, type Subscription } from '@/lib/dataProvider'
import { logger } from '@/lib/logger'

// Screen size state reported by a display presenter instance
export interface DisplayScreenSize {
  displayId: string
  instanceId: string // Unique ID for this presenter instance
  width: number
  height: number
  timestamp: number
}

// How long to consider a screen size report valid (30 seconds)
const STALE_THRESHOLD_MS = 30000

// Generate a random instance ID
const generateInstanceId = () => {
  return Math.random().toString(36).substring(2, 8)
}

export const useDisplayScreenSizeStore = defineStore('displayScreenSize', () => {
  // ============================================
  // STATE
  // ============================================
  
  // Map of displayId -> Map of instanceId -> screen size info (for editor/manager view)
  // Allows tracking multiple presenter instances per display
  const screenSizes = ref<Map<string, Map<string, DisplayScreenSize>>>(new Map())
  
  // Editor subscriptions (listening to presenter broadcasts)
  const editorSubscriptions = ref<Map<string, Subscription>>(new Map())
  
  // Presenter state
  const presenterSubscription = ref<Subscription | null>(null)
  let currentDisplayId: string | null = null
  let currentInstanceId: string | null = null
  let currentWidth = 0
  let currentHeight = 0

  // ============================================
  // GETTERS
  // ============================================
  
  // Get all active screen sizes for a specific display
  const getScreenSizes = (displayId: string): DisplayScreenSize[] => {
    const instanceMap = screenSizes.value.get(displayId)
    if (!instanceMap) return []
    
    const now = Date.now()
    const active: DisplayScreenSize[] = []
    
    for (const [, size] of instanceMap) {
      if (now - size.timestamp <= STALE_THRESHOLD_MS) {
        active.push(size)
      }
    }
    
    return active
  }
  
  // Get all active screen sizes across all displays
  const allScreenSizes = computed(() => {
    const now = Date.now()
    const active: DisplayScreenSize[] = []
    
    for (const [, instanceMap] of screenSizes.value) {
      for (const [, size] of instanceMap) {
        if (now - size.timestamp <= STALE_THRESHOLD_MS) {
          active.push(size)
        }
      }
    }
    
    return active
  })

  // ============================================
  // PRESENTER FUNCTIONS (for PresentView)
  // ============================================
  
  // Subscribe as presenter - listen for size requests and respond
  const subscribeAsPresenter = async (
    displayId: string,
    initialWidth: number,
    initialHeight: number
  ): Promise<Subscription> => {
    currentDisplayId = displayId
    currentInstanceId = generateInstanceId()
    currentWidth = initialWidth
    currentHeight = initialHeight
    
    const channelName = `display-screen-size:${displayId}`
    const provider = getDataProvider()
    
    // Subscribe to channel and listen for size requests
    const subscription = provider.subscribeToChannel(channelName, {
      'request-size': () => {
        logger.info(`[DisplayScreenSize] Received size request for display ${displayId} (instance ${currentInstanceId})`)
        broadcastScreenSize()
      }
    })
    presenterSubscription.value = subscription
    
    // Broadcast immediately so editors know we're active
    broadcastScreenSize()
    
    logger.info(`[DisplayScreenSize] Subscribed as presenter for display ${displayId} (instance ${currentInstanceId})`)
    return subscription
  }
  
  // Unsubscribe presenter
  const unsubscribeAsPresenter = async () => {
    if (presenterSubscription.value) {
      presenterSubscription.value.unsubscribe()
      presenterSubscription.value = null
    }
    currentDisplayId = null
    currentInstanceId = null
  }
  
  // Update screen size (call on resize) - broadcasts immediately
  const updateScreenSize = (width: number, height: number) => {
    // Only broadcast if size actually changed
    if (width === currentWidth && height === currentHeight) {
      return
    }
    
    currentWidth = width
    currentHeight = height
    
    // Broadcast immediately on size change
    broadcastScreenSize()
  }
  
  // Broadcast current screen size
  const broadcastScreenSize = async () => {
    if (!currentDisplayId || !currentInstanceId) return
    
    const state: DisplayScreenSize = {
      displayId: currentDisplayId,
      instanceId: currentInstanceId,
      width: currentWidth,
      height: currentHeight,
      timestamp: Date.now()
    }
    
    const provider = getDataProvider()
    const channelName = `display-screen-size:${currentDisplayId}`
    await provider.broadcast(channelName, 'screen-size', state)
    
    logger.debug(`[DisplayScreenSize] Broadcast screen size: ${currentWidth}x${currentHeight} (instance ${currentInstanceId})`)
  }
  
  // ============================================
  // EDITOR FUNCTIONS (for Manage Displays view)
  // ============================================
  
  // Subscribe to a display's screen size broadcasts
  const subscribeToDisplay = async (displayId: string): Promise<void> => {
    if (editorSubscriptions.value.has(displayId)) {
      // Already subscribed, just request current size
      requestScreenSize(displayId)
      return
    }
    
    const channelName = `display-screen-size:${displayId}`
    const provider = getDataProvider()
    
    const subscription = provider.subscribeToChannel(channelName, {
      'screen-size': (payload: DisplayScreenSize) => {
        logger.debug(`[DisplayScreenSize] Received screen size for ${displayId} (instance ${payload.instanceId}):`, payload)
        
        // Get or create instance map for this display
        let instanceMap = screenSizes.value.get(displayId)
        if (!instanceMap) {
          instanceMap = new Map()
          screenSizes.value.set(displayId, instanceMap)
        }
        
        instanceMap.set(payload.instanceId, payload)
      }
    })
    
    editorSubscriptions.value.set(displayId, subscription)
    logger.info(`[DisplayScreenSize] Subscribed to display ${displayId} screen size`)
    
    // Request current size immediately
    requestScreenSize(displayId)
  }
  
  // Request screen size from a display
  const requestScreenSize = async (displayId: string): Promise<void> => {
    const provider = getDataProvider()
    const channelName = `display-screen-size:${displayId}`
    await provider.broadcast(channelName, 'request-size', {})
    logger.debug(`[DisplayScreenSize] Requested screen size from display ${displayId}`)
  }
  
  // Request screen sizes from all subscribed displays
  const requestAllScreenSizes = async (): Promise<void> => {
    for (const [displayId] of editorSubscriptions.value) {
      await requestScreenSize(displayId)
    }
  }
  
  // Unsubscribe from a display
  const unsubscribeFromDisplay = async (displayId: string): Promise<void> => {
    const subscription = editorSubscriptions.value.get(displayId)
    if (subscription) {
      subscription.unsubscribe()
      editorSubscriptions.value.delete(displayId)
      screenSizes.value.delete(displayId)
    }
  }
  
  // Unsubscribe from all displays
  const unsubscribeFromAllDisplays = async (): Promise<void> => {
    for (const [displayId] of editorSubscriptions.value) {
      await unsubscribeFromDisplay(displayId)
    }
  }
  
  // ============================================
  // CLEANUP
  // ============================================
  
  const cleanupPresenter = async () => {
    await unsubscribeAsPresenter()
  }
  
  const cleanupEditor = async () => {
    await unsubscribeFromAllDisplays()
  }

  return {
    // State
    screenSizes,
    
    // Getters
    getScreenSizes,
    allScreenSizes,
    
    // Presenter functions
    subscribeAsPresenter,
    unsubscribeAsPresenter,
    updateScreenSize,
    broadcastScreenSize,
    cleanupPresenter,
    
    // Editor functions
    subscribeToDisplay,
    unsubscribeFromDisplay,
    unsubscribeFromAllDisplays,
    requestScreenSize,
    requestAllScreenSizes,
    cleanupEditor
  }
})
